// whocare: the card a long-form article status shows in timelines instead of its text, media and link card.
// It is rendered by components/status.jsx (edits/display.mjs), so it ships in the main bundle: keep it lean
// (no data fetching, no extra stock imports beyond Blurhash and Icon).
// Two sizes from one component: the full card (timelines, threads, notifications that show the whole post) and
// `compact` (quote cards, grouped notification summaries, the three-pane "Following" list, the reply indicator):
// title, one line of excerpt, a small thumbnail. Without `href` the card is not a link but plain content, for
// places that already sit inside a link or a clickable row (which then opens the post itself).
import { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import classNames from 'classnames';

import ChevronRightIcon from '@/material-icons/400-24px/chevron_right.svg?react';
import DescriptionIcon from '@/material-icons/400-24px/description.svg?react';
import { Blurhash } from 'mastodon/components/blurhash';
import { Icon } from 'mastodon/components/icon';
import { useBlurhash } from 'mastodon/initial_state';

import type { ArticleCard, ArticleCover } from '../types';

import classes from './article_preview_card.module.scss';
import { messages } from './messages';

export const ArticleCoverThumb: React.FC<{
  cover: ArticleCover;
  hidden?: boolean;
  className?: string;
}> = ({ cover, hidden, className }) => {
  const [loaded, setLoaded] = useState(false);
  const handleLoad = useCallback(() => {
    setLoaded(true);
  }, []);

  return (
    // spans (display: block in the CSS): the card may sit inside a link or a <span> row, where <div> is invalid
    <span className={classNames(classes.cover, className)}>
      {cover.blurhash && (
        <Blurhash
          hash={cover.blurhash}
          className={classes.coverBlurhash}
          dummy={!useBlurhash}
        />
      )}
      {!hidden && (
        <img
          src={cover.preview_url}
          alt=''
          loading='lazy'
          decoding='async'
          onLoad={handleLoad}
          className={classNames(
            classes.coverImage,
            loaded && classes.coverImageLoaded,
          )}
        />
      )}
    </span>
  );
};

export const ArticlePreviewCard: React.FC<{
  article: ArticleCard;
  /**
   * Path of the status (`/@acct/id`), so the card is a real link (middle click, copy link). Leave it out where
   * the card sits inside something that is already a link or a clickable row: it then renders as plain content.
   */
  href?: string;
  /** Opens the status like a click on its text does (the three-pane layout opens it in the post column). */
  onOpen?: (e: React.MouseEvent) => void;
  /** Quotes and summaries: smaller title, one line of excerpt, small thumbnail, no "Read more". */
  compact?: boolean;
  /** Sensitive media the viewer has not revealed: show only the blurhash of the cover. */
  hideCover?: boolean;
  lang?: string;
}> = ({ article, href, onOpen, compact, hideCover, lang }) => {
  const intl = useIntl();

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      // leave modified clicks to the browser (new tab / window), like any link
      if (
        !onOpen ||
        e.button !== 0 ||
        e.ctrlKey ||
        e.metaKey ||
        e.shiftKey ||
        e.altKey
      ) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      onOpen(e);
    },
    [onOpen],
  );

  // with a subtitle the server makes it the excerpt; compact shows that one line either way
  const showExcerpt =
    !!article.excerpt && (compact || article.excerpt !== article.subtitle);

  const content = (
    <>
      <span className={classes.text}>
        <span className={classes.pill}>
          <Icon id='article' icon={DescriptionIcon} />
          {intl.formatMessage(messages.pill, {
            minutes: article.reading_minutes,
          })}
        </span>
        <span className={classes.title}>{article.title}</span>
        {article.subtitle && !compact && (
          <span className={classes.subtitle}>{article.subtitle}</span>
        )}
        {/* the server uses the subtitle as excerpt when there is one: do not print it twice */}
        {showExcerpt && (
          <span className={classes.excerpt}>{article.excerpt}</span>
        )}
        {!compact && (
          <span className={classes.readMore}>
            {intl.formatMessage(messages.readMore)}
            <Icon id='chevron-right' icon={ChevronRightIcon} />
          </span>
        )}
      </span>

      {article.cover && (
        <ArticleCoverThumb cover={article.cover} hidden={hideCover} />
      )}
    </>
  );

  if (!href) {
    return (
      <span
        lang={lang}
        className={classNames(
          classes.card,
          classes.static,
          compact && classes.compact,
        )}
      >
        {content}
      </span>
    );
  }

  return (
    // `collection-preview` borrows the stock spacing rules of the upstream collection card (margin above the card,
    // the thread indent and the notification gutter), so the card lines up wherever a status is rendered.
    <a
      href={href}
      onClick={handleClick}
      lang={lang}
      className={classNames(
        classes.card,
        'collection-preview',
        compact && classes.compact,
      )}
    >
      {content}
    </a>
  );
};
